/**
 * Phase 7A + 7B FE — Visit Detail "Forms" section (data-entry runtime).
 *
 * Lists every published form on the visit's study and, for each,
 * either the existing form instance for the (subject, visit, form)
 * triple OR a "Start" action. Clicking a row opens the data-entry
 * modal that renders the field definitions + collapses the response
 * trail to current values + save-draft-as-you-type + submit.
 *
 * Phase 7B (AC5-7): every saveDraft response includes the per-field
 * `violations` list so we can render error borders + messages inline
 * on blur. Submit failures also carry the same shape via
 * ApiError.details.violations and the submit button stays disabled
 * until every violation is resolved.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Download,
  FileEdit,
  History,
  Loader2,
  Save,
  ShieldCheck,
  Undo2,
  X,
} from 'lucide-react';
import { fmtDateTime } from '@/lib/dates';
import {
  ApiError,
  forms,
  formInstances,
  visitLifecycle,
  type FormFieldDefinitionRow,
  type FormFieldResponseRow,
  type FormFieldViolation,
  type FormInstanceStatus,
  type FormRow,
} from '@/api/client';
import { computeHiddenFieldIds as clientComputeHiddenFieldIds } from '@/lib/formConditional';
import { RaiseFieldQueryButton } from '@/pages/visits/RaiseFieldQueryButton';
import { errorText } from '@/lib/permissionError';
import { useHasRole } from '@/components/RoleGate';

/** Who fills in — and so may correct — a form (server checks the study role). */
const DATA_ENTRY_ROLES = ['coordinator', 'principal_investigator', 'sub_investigator'] as const;

const STATUS_LABEL: Record<FormInstanceStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

const STATUS_BADGE: Record<FormInstanceStatus, string> = {
  not_started: 'bg-slate-50 text-slate-600 border-slate-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function VisitFormsSection({
  subjectId,
  visitId,
  studyId,
}: {
  subjectId: string;
  visitId: string;
  studyId: string;
}) {
  const [openForm, setOpenForm] = useState<FormRow | null>(null);
  // Monitors/auditors only read: they can open a form that has data, never
  // start a new one (the start endpoint is data entry).
  const canEnterData = useHasRole(DATA_ENTRY_ROLES);

  // Phase 7E AC16 (policy A) — narrow to forms bound to THIS visit's
  // template. BE resolves visit_id → template_visit_id and filters the
  // list server-side so unbound forms and forms for other visits are
  // never returned. Coordinators see exactly the forms the designer
  // intended for the visit.
  const formsQ = useQuery({
    queryKey: ['forms', 'for-visit', studyId, visitId, subjectId],
    queryFn: () => forms.list({ study_id: studyId, visit_id: visitId, subject_id: subjectId, status: 'published', limit: 200 }),
    enabled: !!studyId && !!visitId,
  });

  const rows = formsQ.data?.rows ?? [];

  const downloadPdf = useMutation({
    mutationFn: () => forms.visitPdf(subjectId, visitId),
    onSuccess: (r) => {
      const a = document.createElement('a');
      a.href = r.download_url;
      a.rel = 'noopener';
      a.click();
      toast.success(`Downloading ${r.file_name} (${r.form_count} forms).`);
    },
    onError: (e) => toast.error(errorText(e, 'Could not build the PDF.')),
  });

  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center gap-2">
        <FileEdit className="h-4 w-4 text-brand-500" />
        <div>
          <h2 className="text-sm font-semibold text-ink-primary">eSource forms</h2>
          <p className="text-xs text-ink-muted">
            Published forms for this study. Open a form to capture visit data.
          </p>
        </div>
        <button
          type="button"
          onClick={() => downloadPdf.mutate()}
          disabled={downloadPdf.isPending || rows.length === 0}
          className="ml-auto flex items-center gap-1.5 rounded-lg border border-surface-border px-2.5 py-1.5 text-[12px] font-medium text-ink-secondary hover:bg-surface-muted/50 disabled:opacity-40"
          title="Download all visit forms as a single PDF"
        >
          {downloadPdf.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Download PDF
        </button>
      </div>

      {formsQ.isLoading ? (
        <div className="text-xs text-ink-muted">Loading…</div>
      ) : formsQ.data?.esource_restricted ? (
        <p className="text-xs text-ink-muted">eSource is not available to unblinded staff on this study.</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-ink-muted">No published forms yet on this study.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((row) => (
            <li
              key={row.form_id}
              className="flex items-center justify-between rounded border border-surface-border bg-surface-card px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-ink-secondary">{row.reference_number}</span>
                  {row.instance_status && (
                    <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[row.instance_status]}`}>
                      {STATUS_LABEL[row.instance_status]}
                    </span>
                  )}
                </div>
                <div className="text-xs text-ink-primary">{row.name}</div>
              </div>
              {(row.instance_id || canEnterData) && (
                <button
                  type="button"
                  onClick={() => setOpenForm(row)}
                  className="ml-3 shrink-0 rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
                >
                  Open <ArrowRight className="ml-0.5 inline h-3 w-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {openForm && (
        <FormDataEntryModal
          form={openForm}
          subjectId={subjectId}
          visitId={visitId}
          studyId={studyId}
          onClose={() => setOpenForm(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function FormDataEntryModal({
  form,
  subjectId,
  visitId,
  studyId,
  onClose,
}: {
  form: FormRow;
  subjectId: string;
  visitId: string;
  studyId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  // A form that already has an instance opens straight from GET
  // /form-instances/{id} — that's all a monitor/auditor may call. Only a form
  // with no instance yet goes through start, which creates one (data entry).
  const startQ = useQuery({
    queryKey: ['form-instance', 'start', subjectId, visitId, form.form_id],
    queryFn: () => formInstances.start(subjectId, visitId, form.form_id),
    enabled: !form.instance_id,
  });

  const instanceId = form.instance_id || startQ.data?.instance_id;

  const instanceQ = useQuery({
    queryKey: ['form-instance', instanceId],
    queryFn: () => formInstances.get(instanceId!),
    enabled: !!instanceId,
  });

  const definitions = instanceQ.data?.field_definitions ?? [];
  const currentValues = instanceQ.data?.current_values ?? [];
  const status = instanceQ.data?.instance.status ?? 'not_started';

  // A signed visit's forms are read-only until the PI/SI reopens it; on an
  // unlocked visit a submitted form can be opened for correction (14 Oct 2026).
  const lockQ = useQuery({
    queryKey: ['visit', 'sign-history', visitId],
    queryFn: () => visitLifecycle.signHistory(visitId),
  });
  const visitUnlocked = (lockQ.data?.lock_state ?? 'unlocked') === 'unlocked';
  const canEnterData = useHasRole(DATA_ENTRY_ROLES);
  const [correcting, setCorrecting] = useState(false);
  const [correctReason, setCorrectReason] = useState('');

  // Local edit state — keyed by field_id, values are the FE-side
  // representation. Loaded from currentValues on first render.
  const [values, setValues] = useState<Record<string, string | number | string[] | null>>({});
  const [dirty, setDirty] = useState<Set<string>>(() => new Set());
  const [reasons, setReasons] = useState<Record<string, string>>({});
  // Phase 7B — per-field violations returned by saveDraft (soft) and
  // populated from a submit ValidationError.details.violations too.
  const [violations, setViolations] = useState<FormFieldViolation[]>([]);
  // Bug found live (UAT): `violations` only refreshes on a save/submit
  // round trip, so a field's server-reported error used to keep showing
  // (and kept blocking Submit) even after the user had already typed a
  // fix locally — the stale verdict outlived the edit that invalidated
  // it. A field with a pending local edit hasn't been re-validated yet,
  // so its old violation can't be trusted either way; drop it until the
  // next save tells us the real answer.
  const effectiveViolations = useMemo(
    () => violations.filter((v) => !dirty.has(v.field_id)),
    [violations, dirty],
  );
  const violationByField = useMemo(() => {
    const m = new Map<string, FormFieldViolation>();
    for (const v of effectiveViolations) m.set(v.field_id, v);
    return m;
  }, [effectiveViolations]);

  const conditionalRules = instanceQ.data?.conditional_rules ?? [];
  const subjectAttrs = instanceQ.data?.subject_attrs ?? {};
  const studyAttrs = instanceQ.data?.study_attrs ?? {};

  const currentByField = useMemo(() => {
    const map = new Map<string, FormFieldResponseRow>();
    for (const row of currentValues) map.set(row.field_id, row);
    return map;
  }, [currentValues]);

  const hiddenFieldIds = useMemo(() => {
    if (definitions.length === 0 || conditionalRules.length === 0) return new Set<string>();
    const valuesByFieldId: Record<string, unknown> = {};
    for (const def of definitions) {
      if (values[def.field_id] !== undefined) {
        valuesByFieldId[def.field_id] = values[def.field_id];
      } else {
        const row = currentByField.get(def.field_id);
        if (row) valuesByFieldId[def.field_id] = valueFromRow(row);
      }
    }
    return clientComputeHiddenFieldIds(conditionalRules, {
      defs: definitions,
      valuesByFieldId,
      subjectAttrs,
      studyAttrs,
    });
  }, [definitions, conditionalRules, values, currentByField, subjectAttrs, studyAttrs]);

  // Phase 8 AC13 — field-history drawer.
  const [historyField, setHistoryField] = useState<{
    instanceId: string;
    fieldId: string;
  } | null>(null);

  // Phase 8 AC5 — undo availability per field. Trail length ≥ 2 means
  // there's a previous saved value to restore; latest row being an
  // undo itself is treated as no-further-undo (server refuses too).
  const undoAvailability = useMemo(() => {
    const history = instanceQ.data?.response_history ?? [];
    const byField = new Map<string, FormFieldResponseRow[]>();
    for (const row of history) {
      const list = byField.get(row.field_id);
      if (list) list.push(row);
      else byField.set(row.field_id, [row]);
    }
    const canUndo = new Map<string, boolean>();
    for (const [fid, rows] of byField.entries()) {
      const latest = rows[rows.length - 1];
      const hasPrevious = rows.length >= 2;
      const latestIsUndo = !!(latest && (latest as { undo_of_response_id?: string | null }).undo_of_response_id);
      canUndo.set(fid, hasPrevious && !latestIsUndo);
    }
    return canUndo;
  }, [instanceQ.data?.response_history]);

  // Bug found live (UAT audit): this used to seed `values` ONCE (guarded
  // by "only if currently empty") and never touch it again — so after a
  // refetch (save success, undo, another tab's change, or a field whose
  // DB insert silently failed server-side), the local copy permanently
  // drifted from the database. That's what let the Submit gate read
  // "clean" while a required field was actually still empty, and what
  // made an Undo'd field render blank instead of its restored value.
  // Now: always reconcile from the server for any field that isn't
  // actively being edited locally (`dirty`) — a field mid-edit is left
  // alone so a refetch triggered by saving a DIFFERENT field can't wipe
  // out what the user is currently typing.
  useEffect(() => {
    if (currentValues.length === 0) return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const row of currentValues) {
        if (dirty.has(row.field_id)) continue;
        const serverValue = valueFromRow(row);
        if (!(row.field_id in prev) || !valuesEqual(prev[row.field_id], serverValue)) {
          next[row.field_id] = serverValue;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [currentValues, dirty]);

  const save = useMutation({
    mutationFn: () => {
      const responses = Array.from(dirty).map((fieldId) => ({
        field_id: fieldId,
        value: values[fieldId] ?? null,
        correction_reason: reasons[fieldId]?.trim() || undefined,
      }));
      return formInstances.saveDraft(instanceId!, { responses });
    },
    onSuccess: (r) => {
      // Bug found live (UAT audit): a per-field DB insert can fail even
      // when the overall request succeeds — previously that field's
      // value was silently dropped, with no error shown and its `dirty`
      // flag cleared as if it had saved. Keep failed fields dirty (with
      // whatever correction reason they had) so retrying is just
      // clicking Save again, not re-typing anything.
      const failedIds = new Set(r.failed_fields.map((f) => f.field_id));
      if (failedIds.size > 0) {
        toast.error(
          `${r.responses_saved} field${r.responses_saved === 1 ? '' : 's'} saved, but ${failedIds.size} failed: ${r.failed_fields.map((f) => f.field_key).join(', ')}. Please try again.`,
        );
        setDirty(failedIds);
        setReasons((prev) => {
          const next: Record<string, string> = {};
          for (const id of failedIds) if (prev[id]) next[id] = prev[id];
          return next;
        });
      } else {
        toast.success(`Saved ${r.responses_saved} field${r.responses_saved === 1 ? '' : 's'}.`);
        setDirty(new Set());
        setReasons({});
      }
      setViolations(r.violations ?? []);
      qc.invalidateQueries({ queryKey: ['form-instance', instanceId] });
    },
    onError: (e) => toast.error(errorText(e, 'Save failed')),
  });

  const submit = useMutation({
    mutationFn: () => formInstances.submit(instanceId!),
    onSuccess: () => {
      setViolations([]);
      toast.success(`Form ${form.reference_number} submitted.`);
      qc.invalidateQueries({ queryKey: ['form-instance', instanceId] });
      qc.invalidateQueries({ queryKey: ['forms', 'for-visit'] });
      // The last form in can make the visit ready for PI signature.
      qc.invalidateQueries({ queryKey: ['visit', 'sign-history'] });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.details && typeof e.details === 'object') {
        const d = e.details as { violations?: FormFieldViolation[] };
        if (Array.isArray(d.violations)) setViolations(d.violations);
      }
      toast.error(errorText(e, 'Submit failed'));
    },
  });

  const correct = useMutation({
    mutationFn: () => formInstances.correct(instanceId!, correctReason.trim()),
    onSuccess: () => {
      toast.success('Form opened for correction. Give a reason for each field you change, then submit again.');
      setCorrecting(false);
      setCorrectReason('');
      qc.invalidateQueries({ queryKey: ['form-instance', instanceId] });
      qc.invalidateQueries({ queryKey: ['forms', 'for-visit'] });
      // No longer ready for PI signature until it is re-submitted.
      qc.invalidateQueries({ queryKey: ['visit', 'sign-history'] });
    },
    onError: (e) => toast.error(errorText(e, 'Could not open the form for correction')),
  });

  function updateValue(fieldId: string, value: string | number | string[] | null) {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    setDirty((prev) => new Set(prev).add(fieldId));
  }

  // Phase 8 AC5 — undo per field. The BE writes a new row copying the
  // previous saved value; on success we clear local state for the field
  // so the refreshed instance re-hydrates it from the server.
  const undo = useMutation({
    mutationFn: (fieldId: string) =>
      formInstances.undoField(instanceId!, fieldId),
    onSuccess: (_r, fieldId) => {
      toast.success('Reverted to previous saved value.');
      setValues((prev) => {
        const copy = { ...prev };
        delete copy[fieldId];
        return copy;
      });
      setDirty((prev) => {
        const copy = new Set(prev);
        copy.delete(fieldId);
        return copy;
      });
      setReasons((prev) => {
        const copy = { ...prev };
        delete copy[fieldId];
        return copy;
      });
      qc.invalidateQueries({ queryKey: ['form-instance', instanceId] });
    },
    onError: (e) => toast.error(errorText(e, 'Undo failed')),
  });

  // 21 CFR Part 11 / ALCOA+ — when a field already has a saved response,
  // changing it requires a non-empty `correction_reason` on the BE. Track
  // the dirty fields that don't yet have one so we can gate Save + guide
  // the user instead of firing a doomed 400. Matches saveDraft.ts:168.
  const fieldsMissingReason = useMemo(() => {
    const s = new Set<string>();
    for (const fieldId of dirty) {
      if (currentByField.has(fieldId) && !(reasons[fieldId] ?? '').trim()) {
        s.add(fieldId);
      }
    }
    return s;
  }, [dirty, currentByField, reasons]);

  const fieldLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const def of definitions) m.set(def.field_id, def.field_label || def.field_key);
    return m;
  }, [definitions]);

  // Bug found live (UAT): Submit was only ever gated by server-reported
  // `violations` (populated after a round trip fails) — there was no
  // client-side required-field check at all, so a form could be
  // submitted with a required field still empty whenever `violations`
  // happened to be stale/empty. Visible (not hidden by a conditional
  // rule) required fields are checked directly against local state,
  // mirroring the backend's own `hasValue()` semantics.
  const missingRequiredFieldIds = useMemo(() => {
    const ids: string[] = [];
    for (const def of definitions) {
      if (hiddenFieldIds.has(def.field_id)) continue;
      const config = safeParseConfig(def.config_json);
      if (!config.required || def.field_type === 'calculated') continue;
      if (!fieldHasValue(values[def.field_id])) ids.push(def.field_id);
    }
    return ids;
  }, [definitions, hiddenFieldIds, values]);

  // PI/SI-only (or delegated-responsibility) forms: everyone on the study can
  // view, only the permitted roles can fill (client, 23 Oct 2026).
  const canFill = instanceQ.data?.can_fill !== false;
  const readOnly = status === 'completed' || !visitUnlocked || !canEnterData || !canFill;
  const canSave =
    dirty.size > 0
    && !save.isPending
    && !readOnly
    && fieldsMissingReason.size === 0;
  const canCorrect = status === 'completed' && visitUnlocked && canEnterData && canFill && !!instanceId;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-primary">
              {form.name}{' '}
              <span className="font-mono text-[11px] text-ink-muted">{form.reference_number}</span>
            </h3>
            <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-ink-muted">
              <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-medium ${STATUS_BADGE[status]}`}>
                <StatusIcon status={status} />
                {STATUS_LABEL[status]}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {startQ.isLoading || instanceQ.isLoading ? (
          <div className="p-8 text-center text-xs text-ink-muted">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading form…
          </div>
        ) : instanceQ.data?.esource_restricted ? (
          <div className="p-8 text-center text-xs text-ink-muted">
            {instanceQ.data.esource_restricted_reason === 'responsibility_required'
              ? 'This form is only available to staff delegated the responsibility it requires.'
              : 'eSource is not available to unblinded staff on this study.'}
          </div>
        ) : startQ.isError || instanceQ.isError ? (
          <div className="p-8 text-center text-xs text-danger-600">
            Failed to load form. {(startQ.error ?? instanceQ.error)?.message ?? ''}
          </div>
        ) : (
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {missingRequiredFieldIds.length > 0 && (
              <div
                className="rounded border border-danger-300 bg-danger-50 px-3 py-2 text-[11px] text-danger-800"
                role="alert"
              >
                <div className="font-semibold">
                  {missingRequiredFieldIds.length} required field{missingRequiredFieldIds.length === 1 ? '' : 's'} still empty.
                </div>
                <div className="mt-0.5 opacity-80">
                  {missingRequiredFieldIds
                    .map((id) => `"${fieldLabelById.get(id) ?? id.slice(0, 8)}"`)
                    .join(', ')}
                </div>
              </div>
            )}
            {effectiveViolations.length > 0 && (
              <div
                className="rounded border border-danger-300 bg-danger-50 px-3 py-2 text-[11px] text-danger-800"
                role="alert"
              >
                <div className="font-semibold">
                  {effectiveViolations.length} field{effectiveViolations.length === 1 ? '' : 's'} need
                  {effectiveViolations.length === 1 ? 's' : ''} attention before submit.
                </div>
                <div className="mt-0.5 opacity-80">
                  See highlighted fields below.
                </div>
              </div>
            )}
            {fieldsMissingReason.size > 0 && (
              <div
                className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
                role="alert"
              >
                <div className="font-semibold">
                  Correction reason required (CFR Part 11) for{' '}
                  {fieldsMissingReason.size} changed field
                  {fieldsMissingReason.size === 1 ? '' : 's'}.
                </div>
                <div className="mt-0.5 opacity-80">
                  {Array.from(fieldsMissingReason)
                    .map((id) => `"${fieldLabelById.get(id) ?? id.slice(0, 8)}"`)
                    .join(', ')}
                  {' '}— Save is disabled until every changed field has a reason.
                </div>
              </div>
            )}
            {definitions.length === 0 ? (
              <p className="text-xs text-ink-muted">
                This form has no fields defined yet.
              </p>
            ) : (
              definitions
                .slice()
                .sort((a, b) => a.field_order - b.field_order)
                // Phase 7D AC15 — hidden fields don't render on the
                // data-entry surface. Values already captured for a
                // now-hidden field are preserved on the response trail
                // per AC15's "preserved, not discarded" clause; the BE
                // stamps `hidden_by_rule_id` on those rows.
                .filter((def) => !hiddenFieldIds.has(def.field_id))
                .map((def) => (
                  <FieldRow
                    key={def.field_id}
                    def={def}
                    formLabel={form.name}
                    studyId={studyId}
                    subjectId={subjectId}
                    visitId={visitId}
                    value={values[def.field_id] ?? null}
                    onChange={(v) => updateValue(def.field_id, v)}
                    hasPrior={currentByField.has(def.field_id) && dirty.has(def.field_id)}
                    correctionReason={reasons[def.field_id] ?? ''}
                    onCorrectionReasonChange={(v) =>
                      setReasons((prev) => ({ ...prev, [def.field_id]: v }))
                    }
                    violation={violationByField.get(def.field_id)}
                    // Phase 8 AC13 — one row per prior response indicates
                    // there's a real trail to show. First-time captures
                    // (no prior row on the collapsed map) hide the icon.
                    hasHistory={currentByField.has(def.field_id)}
                    onOpenHistory={() =>
                      instanceId && setHistoryField({ instanceId, fieldId: def.field_id })
                    }
                    canUndo={!!undoAvailability.get(def.field_id) && !readOnly}
                    undoing={undo.isPending && undo.variables === def.field_id}
                    onUndo={() => undo.mutate(def.field_id)}
                    readOnly={readOnly}
                  />
                ))
            )}
          </div>
        )}
        {historyField && (
          <FieldHistoryDrawer
            instanceId={historyField.instanceId}
            fieldId={historyField.fieldId}
            onClose={() => setHistoryField(null)}
          />
        )}

        {correcting && (
          <div className="space-y-1.5 border-t border-surface-border bg-amber-50/60 px-4 py-3">
            <label htmlFor="correct-form-reason" className="block text-[11px] font-semibold text-amber-900">
              Why does this submitted form need correcting?
            </label>
            <textarea
              id="correct-form-reason"
              value={correctReason}
              onChange={(e) => setCorrectReason(e.target.value)}
              maxLength={1000}
              rows={2}
              placeholder="e.g. Weight entered in lb instead of kg, raised by the PI at review"
              className="w-full rounded border border-amber-300 bg-surface-card px-2 py-1 text-xs"
            />
            <p className="text-[10px] text-amber-900/80">
              The form goes back to In progress. Each field you change will also need its own correction
              reason, the old values stay in the field history, and the visit returns to the PI for
              signature once the form is submitted again.
              {correctReason.trim().length > 0 && correctReason.trim().length < 10 && (
                <span className="ml-1 font-medium text-danger-700">At least 10 characters.</span>
              )}
            </p>
          </div>
        )}

        <footer className="flex items-center justify-between gap-2 border-t border-surface-border px-4 py-3">
          <div className="text-[11px] text-ink-muted">
            {dirty.size > 0 && !readOnly ? (
              <>{dirty.size} unsaved change{dirty.size === 1 ? '' : 's'}</>
            ) : !canFill ? (
              `Read-only view. ${instanceQ.data?.fill_restricted_message ?? ''}`
            ) : !canEnterData ? (
              'Read-only view.'
            ) : !visitUnlocked ? (
              'Visit is signed — the PI or Sub-Investigator must reopen it before any correction.'
            ) : readOnly ? (
              'Submitted — form is read-only.'
            ) : (
              'All changes saved.'
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
            >
              Close
            </button>
            {canCorrect && !correcting && (
              <button
                type="button"
                onClick={() => setCorrecting(true)}
                className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                <FileEdit className="mr-1 inline h-3 w-3" /> Correct form
              </button>
            )}
            {canCorrect && correcting && (
              <>
                <button
                  type="button"
                  onClick={() => { setCorrecting(false); setCorrectReason(''); }}
                  className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => correct.mutate()}
                  disabled={correct.isPending || correctReason.trim().length < 10}
                  className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {correct.isPending ? 'Opening…' : 'Open for correction'}
                </button>
              </>
            )}
            {!readOnly && (
              <>
                <button
                  type="button"
                  onClick={() => save.mutate()}
                  disabled={!canSave}
                  title={
                    fieldsMissingReason.size > 0
                      ? `Enter a "Correction reason" on the ${fieldsMissingReason.size} changed field${fieldsMissingReason.size === 1 ? '' : 's'} before saving`
                      : dirty.size === 0
                        ? 'No changes to save'
                        : 'Save draft'
                  }
                  className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="mr-1 inline h-3 w-3" /> Save draft
                </button>
                <button
                  type="button"
                  onClick={() => submit.mutate()}
                  disabled={
                    submit.isPending
                    || dirty.size > 0
                    || effectiveViolations.length > 0
                    || missingRequiredFieldIds.length > 0
                  }
                  title={
                    dirty.size > 0
                      ? 'Save draft first to include the pending changes'
                      : missingRequiredFieldIds.length > 0
                        ? `Fill in ${missingRequiredFieldIds.length} required field${missingRequiredFieldIds.length === 1 ? '' : 's'} before submit`
                        : effectiveViolations.length > 0
                          ? `Resolve ${effectiveViolations.length} validation issue${effectiveViolations.length === 1 ? '' : 's'} before submit`
                          : 'Submit the form'
                  }
                  className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                >
                  {submit.isPending ? 'Submitting…' : 'Submit'}
                </button>
              </>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: FormInstanceStatus }) {
  if (status === 'completed') return <ShieldCheck className="h-3 w-3" />;
  if (status === 'in_progress') return <Clock className="h-3 w-3" />;
  return <CheckCircle2 className="h-3 w-3 opacity-30" />;
}

function FieldRow({
  def,
  formLabel,
  studyId,
  subjectId,
  visitId,
  value,
  onChange,
  hasPrior,
  correctionReason,
  onCorrectionReasonChange,
  violation,
  hasHistory,
  onOpenHistory,
  canUndo,
  undoing,
  onUndo,
  readOnly,
}: {
  def: FormFieldDefinitionRow;
  formLabel: string;
  studyId: string;
  subjectId: string;
  visitId: string;
  value: string | number | string[] | null;
  onChange: (v: string | number | string[] | null) => void;
  hasPrior: boolean;
  correctionReason: string;
  onCorrectionReasonChange: (v: string) => void;
  violation?: FormFieldViolation;
  hasHistory: boolean;
  onOpenHistory: () => void;
  canUndo: boolean;
  undoing: boolean;
  onUndo: () => void;
  readOnly: boolean;
}) {
  const config = safeParseConfig(def.config_json);
  const required = !!config.required;
  const invalid = !!violation;

  return (
    <div
      className={`group rounded border p-3 ${invalid
        ? 'border-danger-400 bg-danger-50/30'
        : 'border-surface-border bg-surface-card'
        }`}
    >
      <label className="block text-xs">
        <span className="flex items-center justify-between">
          <span className="font-medium text-ink-secondary">
            {def.field_label}
            {required && <span className="ml-1 text-danger-600">*</span>}
          </span>
          <span className="flex items-center gap-0.5">
            <RaiseFieldQueryButton
              fieldId={def.field_id}
              fieldRef={def.field_label || def.field_key}
              formLabel={formLabel}
              studyId={studyId}
              subjectId={subjectId}
              visitId={visitId}
            />
            {canUndo && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (
                    window.confirm(
                      `Undo the last change to "${def.field_label}"? The field will revert to its previous saved value; the undo itself is recorded in the audit trail.`,
                    )
                  ) {
                    onUndo();
                  }
                }}
                disabled={undoing}
                className="rounded p-0.5 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                title="Undo the last change on this field (AC5)"
                aria-label="Undo last change"
              >
                {undoing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Undo2 className="h-3 w-3" />
                )}
              </button>
            )}
            {hasHistory && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onOpenHistory();
                }}
                className="rounded p-0.5 text-ink-muted hover:bg-surface-muted/50 hover:text-ink-primary"
                title="View immutable change history for this field"
                aria-label="View field history"
              >
                <History className="h-3 w-3" />
              </button>
            )}
          </span>
        </span>
        {config.help_text && (
          <span className="mt-0.5 block text-[10px] text-ink-muted">{config.help_text}</span>
        )}
        <div className="mt-1">
          <FieldInput def={def} value={value} onChange={onChange} readOnly={readOnly} />
        </div>
      </label>
      {invalid && (
        <p className="mt-1.5 text-[11px] font-medium text-danger-700" role="alert">
          {violation!.message}
        </p>
      )}
      {hasPrior && (
        <label className="mt-2 block text-[11px]">
          <span className="font-medium text-ink-secondary">
            Correction reason <span className="text-danger-600">*</span>{' '}
            <span className="font-normal text-ink-muted">
              (required by 21 CFR Part 11 to change a previously-saved value)
            </span>
          </span>
          <input
            type="text"
            value={correctionReason}
            onChange={(e) => onCorrectionReasonChange(e.target.value)}
            placeholder="Why is this being changed?"
            aria-required
            aria-invalid={!correctionReason.trim()}
            className={`mt-1 w-full rounded border px-2 py-1 text-xs ${correctionReason.trim()
              ? 'border-amber-300 bg-amber-50/40'
              : 'border-danger-400 bg-danger-50/40'
              }`}
          />
          {!correctionReason.trim() && (
            <span className="mt-1 block text-[10px] font-medium text-danger-700">
              A reason is required before this change can be saved.
            </span>
          )}
        </label>
      )}
    </div>
  );
}

function FieldInput({
  def,
  value,
  onChange,
  readOnly,
}: {
  def: FormFieldDefinitionRow;
  value: string | number | string[] | null;
  onChange: (v: string | number | string[] | null) => void;
  readOnly: boolean;
}) {
  const config = safeParseConfig(def.config_json);
  const options: Array<{ value: string; label: string }> = Array.isArray(config.options)
    ? (config.options as Array<{ value: string; label: string }>)
    : [];
  const disabled = readOnly;

  switch (def.field_type) {
    case 'number':
    case 'calculated':
      return (
        <>
          <input
            type="number"
            disabled={disabled || def.field_type === 'calculated'}
            value={value == null ? '' : String(value)}
            // Bug found live (UAT audit): min/max were enforced server-side
            // at Submit but never threaded into the actual input, so a
            // coordinator got zero real-time feedback while typing.
            min={config.min}
            max={config.max}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full rounded border border-surface-border px-3 py-1.5 text-sm disabled:opacity-70"
          />
          {(config.min !== undefined || config.max !== undefined) && (
            <span className="mt-0.5 block text-[10px] text-ink-muted">
              Range: {config.min ?? '−∞'} – {config.max ?? '∞'}
            </span>
          )}
        </>
      );
    case 'date':
      return (
        <input
          type="date"
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full rounded border border-surface-border px-3 py-1.5 text-sm disabled:opacity-70"
        />
      );
    case 'datetime':
      return (
        <input
          type="datetime-local"
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full rounded border border-surface-border px-3 py-1.5 text-sm disabled:opacity-70"
        />
      );
    case 'textarea':
      return (
        <textarea
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          maxLength={config.max_length}
          rows={3}
          className="w-full rounded border border-surface-border px-3 py-2 text-sm disabled:opacity-70"
        />
      );
    case 'select':
      return (
        <select
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full rounded border border-surface-border px-3 py-1.5 text-sm disabled:opacity-70"
        >
          <option value="">— Select —</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    case 'radio':
      // Bug found live (UAT audit): this used to reuse the exact same
      // <select> dropdown as `select` — data capture was correct, but a
      // designer who picked "Radio" (expecting all options visible at
      // once) got an indistinguishable dropdown instead.
      return (
        <div className="space-y-1">
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-xs">
              <input
                type="radio"
                name={def.field_id}
                disabled={disabled}
                checked={value === o.value}
                onChange={() => onChange(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <div className="space-y-1">
          {options.map((o) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o.value);
            return (
              <label key={o.value} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...arr, o.value]);
                    else onChange(arr.filter((v) => v !== o.value));
                  }}
                />
                <span>{o.label}</span>
              </label>
            );
          })}
        </div>
      );
    case 'text':
    default:
      return (
        <input
          type="text"
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          maxLength={config.max_length}
          className="w-full rounded border border-surface-border px-3 py-1.5 text-sm disabled:opacity-70"
        />
      );
  }
}

// ---------------------------------------------------------------------------

function valueFromRow(row: FormFieldResponseRow): string | number | string[] | null {
  // Bug found live (UAT: date field not showing after submission, then
  // re-entering it wrongly triggered the CFR Part 11 correction-reason
  // prompt). Caspio can round-trip an unset STRING column as '' rather
  // than null (the same quirk hit elsewhere in this codebase) — for a
  // date-type response, value_text is genuinely unset but was masking
  // the real value_date underneath it, so the field looked empty even
  // though a value was actually on file.
  // Bug found live (UAT: submitted checkbox field showed no options
  // checked despite a real value_json on file). Caspio round-trips
  // EVERY unset text-ish column as '' — value_text, value_date,
  // value_json alike — not just value_text as the comment above
  // originally called out. The value_date check was missing the same
  // `!== ''` guard, so a checkbox/select row (value_text: '', value_date:
  // '', real value in value_json) matched the date branch first and
  // returned '' instead of ever reaching value_json.
  if (row.value_text != null && row.value_text !== '') return row.value_text;
  if (row.value_number != null) return row.value_number;
  if (row.value_date != null && row.value_date !== '') return row.value_date;
  if (row.value_json != null && row.value_json !== '') {
    try {
      return JSON.parse(row.value_json) as string[];
    } catch {
      return row.value_json;
    }
  }
  return null;
}

function safeParseConfig(json: string | null | undefined): {
  required?: boolean;
  help_text?: string;
  options?: unknown;
  min?: number;
  max?: number;
  max_length?: number;
  error_message?: string;
} {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function valuesEqual(
  a: string | number | string[] | null | undefined,
  b: string | number | string[] | null,
): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return a === b;
}

/** Mirrors the backend's `hasValue()` (services/forms/validation.ts) so
 *  the Submit gate agrees with what the server will actually enforce —
 *  0 and false-ish scalars are real answers, only null/''/[] count as
 *  "not yet answered". */
function fieldHasValue(value: string | number | string[] | null | undefined): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value !== '';
  if (Array.isArray(value)) return value.length > 0;
  return true; // numbers, including 0, are always a real answer
}

// ---------------------------------------------------------------------------
// Phase 8 AC13 — field-level immutable change-history drawer.

function FieldHistoryDrawer({
  instanceId,
  fieldId,
  onClose,
}: {
  instanceId: string;
  fieldId: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['field-history', instanceId, fieldId],
    queryFn: () => formInstances.fieldHistory(instanceId, fieldId),
  });

  return (
    <div className="fixed inset-y-0 right-0 z-[110] flex w-full max-w-lg flex-col border-l border-surface-border bg-surface-card shadow-xl">
      <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-primary">
            <History className="mr-1 inline h-4 w-4 text-brand-500" />
            Field change history
          </h3>
          {q.data && (
            <p className="mt-0.5 text-[11px] text-ink-muted">
              {q.data.field_label}{' '}
              <span className="font-mono">({q.data.field_key})</span> ·{' '}
              {q.data.count} entr{q.data.count === 1 ? 'y' : 'ies'}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-3">
        {q.isLoading ? (
          <div className="p-6 text-center text-xs text-ink-muted">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading history…
          </div>
        ) : q.isError ? (
          <div className="p-6 text-center text-xs text-danger-600">
            Failed to load history.
          </div>
        ) : (q.data?.entries ?? []).length === 0 ? (
          <p className="p-4 text-center text-xs text-ink-muted">
            No history yet — the field has never been saved.
          </p>
        ) : (
          <ol className="space-y-2">
            {q.data!.entries.map((e) => (
              <li
                key={e.response_id}
                className={`rounded border p-2 text-[11px] ${e.is_create
                  ? 'border-emerald-200 bg-emerald-50/40'
                  : 'border-amber-200 bg-amber-50/40'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-semibold uppercase tracking-wide">
                    {e.is_create ? 'Created' : 'Updated'}{' '}
                    <span className="ml-1 font-mono normal-case text-ink-muted">
                      {e.reference_number}
                    </span>
                  </div>
                  <div className="text-[10px] text-ink-muted">
                    {fmtDateTime(e.changed_at)}
                  </div>
                </div>
                {!e.is_create && (
                  <div className="mt-1">
                    <div className="text-[10px] text-ink-muted">Previous</div>
                    <div className="font-mono text-[11px]">
                      {displayValue(e.old_value)}
                    </div>
                  </div>
                )}
                <div className="mt-1">
                  <div className="text-[10px] text-ink-muted">
                    {e.is_create ? 'Value' : 'New'}
                  </div>
                  <div className="font-mono text-[11px]">
                    {displayValue(e.new_value)}
                  </div>
                </div>
                {e.correction_reason && (
                  <div className="mt-1">
                    <div className="text-[10px] text-ink-muted">
                      Reason for change
                    </div>
                    <div className="text-[11px]">{e.correction_reason}</div>
                  </div>
                )}
                <div className="mt-1 text-[10px] text-ink-muted">
                  {e.changed_by_name ?? e.changed_by}
                  {e.changed_by_email && (
                    <> · <span className="font-mono">{e.changed_by_email}</span></>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function displayValue(
  v: {
    value_text: string | null;
    value_number: number | null;
    value_date: string | null;
    value_json: string | null;
  } | null,
): string {
  if (!v) return '(none)';
  if (v.value_text !== null && v.value_text !== '') return v.value_text;
  if (v.value_number !== null) return String(v.value_number);
  if (v.value_date !== null && v.value_date !== '') return v.value_date;
  if (v.value_json !== null && v.value_json !== '') {
    try {
      const parsed = JSON.parse(v.value_json);
      return Array.isArray(parsed) ? parsed.join(', ') : v.value_json;
    } catch {
      return v.value_json;
    }
  }
  return '(blank)';
}
